import { useTranslation } from "@coworker/locales";
import { MouseEvent, useCallback, useMemo, useState } from "react";
import FixaButton from "@coworker/apprestructured/src/shared/wrappers/FixaButton/FixaButton";
import { ProductSearchHistory } from "@coworker/apprestructured/src/scan-and-search/components/history/ProductSearchHistory";
import { SearchResults } from "@coworker/apprestructured/src/scan-and-search/components/searchResults/SearchResults";
import { Item } from "@coworker/apprestructured/src/shared/types/item";
import CameraIconPath from "@coworker/apprestructured/src/shared/wrappers/FixaSSRIcon/paths/CameraIconPath";
import { FixaToast } from "@coworker/apprestructured/src/shared/wrappers/FixaToast/FixaToast";
import { LoadingArticle } from "./LoadingArticle/LoadingArticle";
import { Camera } from "./Camera/Camera";
import { isAnyRDT } from "@coworker/reusable/helpers/devices";
import { Scanner } from "./Scanner/Scanner";
import { SelectedArticles } from "./SelectedArticles/SelectedArticles";
import { SelectedArticlesHeader } from "./SelectedArticles/SelectedArticlesHeader";
import { fetchPIPHome } from "@coworker/apprestructured/src/shared/hooks/item/usePIPHome";
import ListItem from "@coworker/apprestructured/src/task-management/components/TaskCreatorAndEditorSheet/ListItem/ListItem";
import ImagePreviewAddOn from "@coworker/apprestructured/src/task-management/components/TaskCreatorAndEditorSheet/ListItem/ImagePreviewAddOn/ImagePreviewAddOn";
import { SearchItemCardRenderType } from "@coworker/apprestructured/src/scan-and-search/types/SearchItemCardRenderType";
import { FixaModal } from "@coworker/apprestructured/src/shared/wrappers/FixaModal/FixaModal";
import { FixaSheets } from "@coworker/apprestructured/src/shared/wrappers/FixaModal/FixaSheets";
import { FixaModalHeader } from "@coworker/apprestructured/src/shared/wrappers/FixaModal/FixaModalHeader";
import FixaSearch from "@coworker/apprestructured/src/shared/wrappers/FixaSearch/FixaSearch";
import FixaStatus from "@coworker/apprestructured/src/shared/wrappers/FixaStatus/FixaStatus";
import { FixaModalBody } from "@coworker/apprestructured/src/shared/wrappers/FixaModal/FixaModalBody";
import ModalColumn from "@coworker/apprestructured/src/task-management/components/ModalColumn/ModalColumn";
import StyledModalFooter from "../StyledModalFooter/StyledModalFooter";
import useSaveGuardedFunction from "@coworker/apprestructured/src/save-guard/useSaveGuardedFunction";

type AttachArticlesProps = {
  itemNos: string[];
  setItemNos: (newItemNos: string[]) => void;
  disabled: boolean;
};

export const AttachArticles = ({
  itemNos,
  setItemNos,
  disabled,
}: AttachArticlesProps) => {
  const [showModal, setShowModal] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [itemByNo, setItemByNo] = useState<Record<string, Item>>({});
  const [showSearchHistory, setShowSearchHistory] = useState(false);
  const [showCamera, setShowCamera] = useState(false);
  const [showToast, setShowToast] = useState(false);
  const [toastMessage, setToastMessage] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [modalItemNos, setModalItemNos] = useState<string[]>(itemNos);

  const {
    saveGuardedFunction: handleCloseClick,
    enableSaveGuard,
    disableSaveGuard,
  } = useSaveGuardedFunction(
    useCallback(() => {
      setShowModal(false);
      setShowToast(false);
      setToastMessage("");
    }, [])
  );

  const handleListViewItemClick = useCallback(() => {
    setShowModal(true);
    setModalItemNos(itemNos);
    setSearchQuery("");
  }, [itemNos]);

  const isScannerReady = isAnyRDT();

  const { t } = useTranslation();

  const selectedItems = useMemo(
    () =>
      itemNos
        .map((no) => itemByNo[no])
        .filter((item): item is Item => Boolean(item)),
    [itemNos, itemByNo]
  );

  const selectedModalItems = useMemo(
    () =>
      modalItemNos
        .map((no) => itemByNo[no])
        .filter((item): item is Item => Boolean(item)),
    [modalItemNos, itemByNo]
  );

  const handleItemClick = useCallback(
    (item: Item) => {
      const articleNumber = item.no;
      if (!modalItemNos.includes(articleNumber)) {
        setModalItemNos([...modalItemNos, articleNumber]);
        setItemByNo((prev) => ({ ...prev, [articleNumber]: item }));
        enableSaveGuard();
      } else {
        setToastMessage("articleAlreadyExistsString");
        setShowToast(true);
      }
      setSearchQuery("");
    },
    [modalItemNos, enableSaveGuard]
  );

  const handleBlur = useCallback(() => {
    setShowSearchHistory(false);
  }, []);

  // This prevents the onBlur to run if a history item is clicked
  const handleHistoryItemMouseDown = useCallback(
    (e: MouseEvent<HTMLDivElement>) => {
      e.preventDefault();
    },
    []
  );

  const handleDeleteArticle = useCallback(
    (no: string) => {
      setModalItemNos(modalItemNos.filter((id) => id !== no));
      enableSaveGuard();
    },
    [modalItemNos, enableSaveGuard]
  );

  const handleScannedArticle = useCallback(
    async (articleId?: string) => {
      setShowCamera(false);
      setShowToast(false);

      if (!articleId || isLoading) return;

      if (modalItemNos.includes(articleId)) {
        setToastMessage("articleAlreadyExistsString");
        setShowToast(true);
        return;
      }

      setIsLoading(true);
      try {
        const itemData = await fetchPIPHome(articleId);

        if (itemData) {
          setModalItemNos([...modalItemNos, articleId]);
          setItemByNo((prev) => ({
            ...prev,
            [articleId]: itemData,
          }));
          enableSaveGuard();
        } else {
          setToastMessage("couldNotAddArticleString");
          setShowToast(true);
        }
      } catch {
        setToastMessage("couldNotAddArticleString");
        setShowToast(true);
      } finally {
        setIsLoading(false);
      }
    },
    [isLoading, modalItemNos, enableSaveGuard]
  );

  const imageSrcs = useMemo(() => {
    return selectedItems.map((item) => item.smallImageUrl ?? "");
  }, [selectedItems]);

  const content = useMemo(() => {
    if (showCamera) {
      return <Camera handleScannedArticle={handleScannedArticle} />;
    } else {
      return (
        <FixaModalBody>
          <ModalColumn gap="medium">
            <ModalColumn gap="small">
              <FixaSearch
                id="attach-articles-search"
                placeholder="Search"
                onChange={(e) => setSearchQuery(e.target.value)}
                onClear={() => {
                  setSearchQuery("");
                }}
                onFocus={() => setShowSearchHistory(true)}
                onBlur={handleBlur}
                value={searchQuery}
                actions={[
                  {
                    onClick: () => {
                      setShowToast(false);
                      setShowCamera(true);
                    },
                    ssrIcon: CameraIconPath,
                    id: "camera",
                  },
                ]}
              />
              {isScannerReady && (
                <FixaStatus
                  label={t("scannerReadyString")}
                  statusColor="green"
                  small
                />
              )}
            </ModalColumn>
            <ProductSearchHistory
              hideHistory={searchQuery.length > 0 || !showSearchHistory}
              setSearchQuery={setSearchQuery}
              onHistoryItemMouseDown={handleHistoryItemMouseDown}
            />
            <SearchResults
              searchQuery={searchQuery}
              setSearchQuery={setSearchQuery}
              handleItemCardClick={handleItemClick}
              shouldHideSPRItems={false}
              shouldHideLocalCombinationItems={false}
              itemCardRenderType={SearchItemCardRenderType.button}
            />
            {isLoading ? (
              <LoadingArticle />
            ) : (
              <div>
                <SelectedArticlesHeader
                  numberOfArticles={selectedModalItems.length}
                />
                <SelectedArticles
                  items={selectedModalItems}
                  onDeleteArticle={handleDeleteArticle}
                />
              </div>
            )}
            <Scanner handleScannedArticle={handleScannedArticle} />
          </ModalColumn>
        </FixaModalBody>
      );
    }
  }, [
    handleDeleteArticle,
    handleBlur,
    handleHistoryItemMouseDown,
    handleItemClick,
    handleScannedArticle,
    isLoading,
    isScannerReady,
    searchQuery,
    selectedModalItems,
    showCamera,
    showSearchHistory,
    t,
  ]);

  const handleConfirmClick = useCallback(() => {
    setItemNos(modalItemNos);
    setShowModal(false);
    disableSaveGuard();
  }, [modalItemNos, setItemNos, disableSaveGuard]);

  return (
    <>
      <ListItem
        title={t(`${itemNos.length > 0 ? "articlesString" : "attachArticles"}`)}
        description={
          itemNos.length > 0
            ? itemNos.length === 1
              ? `1 ${t("articleString")}`
              : `${itemNos.length} ${t("articlesString")}`
            : ""
        }
        disabled={disabled}
        onClick={handleListViewItemClick}
        addOn={
          imageSrcs.length > 0 ? <ImagePreviewAddOn srcs={imageSrcs} /> : null
        }
        emphasised={itemNos.length > 0}
      />
      <FixaModal visible={showModal} handleCloseBtn={handleCloseClick}>
        <FixaSheets
          header={
            <FixaModalHeader
              title={t("attachArticles")}
              closeBtnClick={handleCloseClick}
            />
          }
          footer={
            showCamera ? null : (
              <StyledModalFooter>
                <FixaButton
                  text={t("confirmString")}
                  type="primary"
                  onClick={handleConfirmClick}
                />
              </StyledModalFooter>
            )
          }
          fullSize
        >
          {content}
        </FixaSheets>
      </FixaModal>
      <FixaToast
        isOpen={showToast}
        text={t(toastMessage)}
        onCloseRequest={() => setShowToast(false)}
      />
    </>
  );
};
